import type { DsBrandDocumentGenerateStatusResponse } from '@/services/db/ds-brand-document/entity';
import type { BrandWizardDraft, BrandWizardDraftPhase } from '../wizard/draft-store';

/**
 * Shared brand-authoring-run derivation (CON-7724 resume follow-up). One pure
 * function turns the resumable draft (Task 1) plus the latest status poll
 * into a single view state, so the wizard, the Styles-page banner, and the
 * app-wide snackbar (later tasks) all agree on what a run is doing without
 * duplicating the terminal/stall rules three times.
 */

export const AUTHORING_POLL_INTERVAL_MS = 2500;
/** Matches the server's WorkflowExecutionTimeout for the authoring workflow. */
export const AUTHORING_STALL_MS = 25 * 60 * 1000;

export type BrandRunState =
  | { kind: 'authoring'; serverPhase: string | null; startedAt: number; progressUnavailable: boolean }
  | { kind: 'ready' }
  | { kind: 'failed' }
  | { kind: 'sources_expired' }
  | { kind: 'stalled' };

export type BrandRunStatusInput = {
  data: DsBrandDocumentGenerateStatusResponse | undefined;
  isError: boolean;
  errorStatus: number | null;
};

/** Draft phases the server's own terminal statuses map to. */
export type BrandRunOutcomePhase = 'document_review' | 'authoring_failed' | 'sources_expired';

/** Terminal draft phase a status response maps to, or `null` while still in progress. */
export function terminalPhaseFor(data: DsBrandDocumentGenerateStatusResponse): BrandRunOutcomePhase | null {
  if (data.status === 'completed') {
    return (data.document ?? '') === '' ? 'authoring_failed' : 'document_review';
  }
  if (data.status === 'failed') {
    return data.reason === 'no_readable_content' ? 'sources_expired' : 'authoring_failed';
  }
  return null;
}

/**
 * The draft phase a poll result should be PERSISTED as, or `null` while the run
 * is still legitimately in flight. Server outcomes plus the client's own stall
 * verdict: a 404 (the run is gone) or a run that has outlived the server's
 * execution timeout is never going to answer, so it stops being `authoring` —
 * which is what stops the background poll.
 */
export function phaseToPersistFor(
  status: BrandRunStatusInput,
  startedAt: number,
  now: number,
): BrandRunOutcomePhase | 'stalled' | null {
  if (status.data != null) {
    const terminal = terminalPhaseFor(status.data);
    if (terminal != null) return terminal;
  }
  if (status.isError && status.errorStatus === 404) return 'stalled';
  if (now - startedAt > AUTHORING_STALL_MS) return 'stalled';
  return null;
}

function fromPhase(phase: BrandWizardDraftPhase): BrandRunState | null {
  switch (phase) {
    case 'document_review':
      return { kind: 'ready' };
    case 'authoring_failed':
      return { kind: 'failed' };
    case 'sources_expired':
      return { kind: 'sources_expired' };
    case 'stalled':
      return { kind: 'stalled' };
    case 'authoring':
      return null;
    default: {
      const _exhaustive: never = phase;
      return _exhaustive;
    }
  }
}

/** Styles tab badge text for a run state, or `undefined` when there is no live run. */
export function brandRunBadge(state: BrandRunState | null): string | undefined {
  if (state == null) return undefined;
  switch (state.kind) {
    case 'authoring':
      return 'Writing';
    case 'ready':
      return 'Ready';
    case 'failed':
    case 'sources_expired':
    case 'stalled':
      return 'Attention';
    default: {
      const _exhaustive: never = state;
      return _exhaustive;
    }
  }
}

export function deriveBrandRunState(draft: BrandWizardDraft, status: BrandRunStatusInput, now: number): BrandRunState {
  const stored = fromPhase(draft.phase);
  if (stored != null) return stored;
  const persist = phaseToPersistFor(status, draft.startedAt, now);
  if (persist != null) return fromPhase(persist) ?? { kind: 'failed' };
  return {
    kind: 'authoring',
    serverPhase: status.data?.phase ?? null,
    startedAt: draft.startedAt,
    progressUnavailable: status.isError,
  };
}
